import * as React from 'react';
import moment from 'moment-timezone';
import _ from 'lodash';

import { DatePicker, DefaultButton, DialogFooter, Dropdown, Label, Panel, PanelType, PrimaryButton, TextField, Checkbox, Stack, MessageBar, MessageBarType } from '@fluentui/react';

import { Clipboard } from '../core/Clipboard';
import { HealAPI } from '../../healApi';
import { OnSite } from '../../redux/on-site-manager/on-sites/types';
import { Partner } from '../../redux/on-site-manager/partners/types';
import { RootState } from '../../redux/types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '../../redux/message/actions';
import { ServiceOffering } from '../../api/services.types';
import { TimePicker } from '../../components/core/TimePicker';
import { formatDate } from '../../util/Date';
import { DoctorSelector } from '../../components/core/DoctorSelector';
import { useCallback, useEffect, useState } from 'react';

interface Props {
  onClose: (onSite?: OnSite) => void;
  onSite: OnSite;
  partner: Partner;
}

export const OnSiteCreateOrEditPanel = ({ onClose, onSite, partner }: Props) => {
  const [editOnSite, setEditOnSite] = useState(_.cloneDeep(onSite));

  const [dateMoment, setDateMoment] = useState(editOnSite.startDateTime ? moment(editOnSite.startDateTime) : moment());
  const [startTimeMoment, setStartTimeMoment] = useState(editOnSite.startDateTime ? moment(editOnSite.startDateTime) : moment());
  const [endTimeMoment, setEndTimeMoment] = useState(editOnSite.endDateTime ? moment(editOnSite.endDateTime) : moment());

  const [hasBreak, setHasBreak] = useState(!!editOnSite.break);
  const [breakStartTimeMoment, setBreakStartTimeMoment] = useState(editOnSite.break && editOnSite.break.startTime ? moment(editOnSite.break.startTime, 'HH:mm:ss') : moment());
  const [breakEndTimeMoment, setBreakEndTimeMoment] = useState(editOnSite.break && editOnSite.break.endTime ? moment(editOnSite.break.endTime, 'HH:mm:ss') : moment());

  const serviceOfferings = useSelector((state: RootState) => state.serviceOffering.services);
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const isNew = !editOnSite.onsiteId;

  useEffect(() => {
    const datePortion = dateMoment.format(moment.HTML5_FMT.DATE);
    const fmt = `${moment.HTML5_FMT.DATE} ${moment.HTML5_FMT.TIME}`;
    setStartTimeMoment(moment(`${datePortion} ${startTimeMoment.format(moment.HTML5_FMT.TIME)}`, fmt));
    setEndTimeMoment(moment(`${datePortion} ${endTimeMoment.format(moment.HTML5_FMT.TIME)}`, fmt));
    setBreakStartTimeMoment(moment(`${datePortion} ${breakStartTimeMoment.format(moment.HTML5_FMT.TIME)}`, fmt));
    setBreakEndTimeMoment(moment(`${datePortion} ${breakEndTimeMoment.format(moment.HTML5_FMT.TIME)}`, fmt));
    // Intentional to not have breakEndTimeMoment, breakStartTimeMoment, endTimeMoment, startTimeMoment in dependencies (re-entrant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateMoment]);

  const update = (key: string, value: any) => {
    setEditOnSite({ ...editOnSite, [key]: value });
  };

  const onSelectDate = (date: Date | null | undefined) => {
    if (moment.isDate(date)) {
      setDateMoment(moment(date));
    }
  };

  const updateMomentWithDate = useCallback(
    (time: moment.Moment) => {
      const datePortion = dateMoment.format(moment.HTML5_FMT.DATE);
      const fmt = `${moment.HTML5_FMT.DATE} ${moment.HTML5_FMT.TIME}`;

      return moment(`${datePortion} ${time.format(moment.HTML5_FMT.TIME)}`, fmt);
    },
    [dateMoment]
  );

  const canSubmit = () => {
    if (saving) {
      return false;
    }
    const hasRequired =
      dateMoment.isValid() &&
      startTimeMoment.isValid() &&
      endTimeMoment.isValid() &&
      !!editOnSite.name &&
      !!editOnSite.partnerAddressId &&
      !!editOnSite.totalAppointments &&
      !!editOnSite.serviceCode &&
      !!editOnSite.leadDoctorId &&
      (!hasBreak || (hasBreak && breakStartTimeMoment.isValid() && breakEndTimeMoment.isValid()));
    if (isNew) {
      return hasRequired;
    } else {
      return (
        hasRequired &&
        (editOnSite.name !== onSite.name ||
          editOnSite.room !== onSite.room ||
          !startTimeMoment.isSame(moment(onSite.startDateTime)) ||
          !endTimeMoment.isSame(moment(onSite.endDateTime)) ||
          editOnSite.totalAppointments !== onSite.totalAppointments ||
          editOnSite.info !== onSite.info ||
          editOnSite.price !== onSite.price ||
          editOnSite.leadDoctorId !== onSite.leadDoctorId)
      );
    }
  };

  const submit = () => {
    setError(undefined);

    if (hasBreak) {
      if (breakStartTimeMoment.isAfter(breakEndTimeMoment)) {
        setError('Break start time should be before end time');
        return;
      }
      if (breakStartTimeMoment.isBefore(startTimeMoment)) {
        setError('Break should start after on-site start time');
        return;
      }
      if (breakEndTimeMoment.isAfter(endTimeMoment)) {
        setError('Break should end before on-site end time');
        return;
      }
    }

    setSaving(true);

    if (isNew) {
      HealAPI.createOnSiteEvent(
        editOnSite.name,
        editOnSite.partnerAddressId,
        partner.id!,
        startTimeMoment.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
        endTimeMoment.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
        parseInt('' + editOnSite.totalAppointments, 10),
        editOnSite.serviceCode,
        editOnSite.leadDoctorId,
        editOnSite.price,
        editOnSite.info,
        editOnSite.room,
        hasBreak
          ? {
              startTime: breakStartTimeMoment.format('HH:mm'),
              endTime: breakEndTimeMoment.format('HH:mm'),
            }
          : undefined
      )
        .then((response) => {
          if (response.ok && response.data) {
            onClose(response.data);
          } else {
            const content = (response.data || {}).description || 'Something went wrong.';
            dispatch(toast({ type: 'error', content }));
            setSaving(false);
          }
        })
        .catch((error) => {
          dispatch(toast({ type: 'error', content: error }));
          setSaving(false);
        });
    } else {
      HealAPI.editOnSiteEvent(
        editOnSite.onsiteId,
        editOnSite.name,
        startTimeMoment.format('HH:mm'),
        endTimeMoment.format('HH:mm'),
        parseInt('' + editOnSite.totalAppointments, 10),
        editOnSite.room,
        editOnSite.info,
        editOnSite.leadDoctorId,
        editOnSite.price
      )
        .then((response) => {
          if (response.ok && response.data) {
            onClose(response.data);
          } else {
            const content = (response.data || {}).description || 'Something went wrong.';
            dispatch(toast({ type: 'error', content }));
            setSaving(false);
          }
        })
        .catch((error) => {
          dispatch(toast({ type: 'error', content: error }));
          setSaving(false);
        });
    }
  };

  return (
    <Panel isOpen={true} type={PanelType.medium} headerText={isNew ? 'Add on-site' : 'Edit on-site'} isHiddenOnDismiss={true} onDismiss={() => onClose()}>
      <Stack tokens={{ childrenGap: 6 }}>
        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
        <TextField label="On-Site event name" required={true} value={editOnSite.name} onChange={(ev, value) => update('name', value)} data-tid="inp_name" />
        <Dropdown
          label="Campus"
          required={true}
          placeholder="Select campus name"
          options={partner.addresses.map((a) => ({
            key: a.id!,
            text: `${a.name}  (${a.fullAddress})`,
          }))}
          selectedKey={editOnSite.partnerAddressId}
          disabled={!isNew}
          onChange={(ev, opt) => opt && update('partnerAddressId', opt.key)}
          data-tid="dd_campus"
        />
        <TextField label="Campus room" required={false} value={editOnSite.room} onChange={(ev, value) => update('room', value)} data-tid="inp_campusRoom" />
        <Stack horizontal tokens={{ childrenGap: 6 }}>
          <DatePicker
            label="Date"
            isRequired={true}
            disabled={!isNew}
            placeholder="Select a date"
            value={dateMoment.toDate()}
            onSelectDate={onSelectDate}
            minDate={new Date()}
            formatDate={formatDate}
          />
          <TimePicker required={true} label="Start time" value={startTimeMoment} onChange={(m) => setStartTimeMoment(updateMomentWithDate(m))} />
          <TimePicker required={true} label="End time" value={endTimeMoment} onChange={(m) => setEndTimeMoment(updateMomentWithDate(m))} />
        </Stack>

        <Stack horizontal tokens={{ childrenGap: 6 }}>
          <Stack.Item grow={1}>
            <Label>Break</Label>
            <Checkbox
              styles={{ root: { marginTop: 5, marginBottom: 8 } }}
              label="Create a provider break. No bookings can be made in the window."
              checked={hasBreak}
              disabled={!isNew}
              onChange={() => setHasBreak(!hasBreak)}
            />
          </Stack.Item>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 6 }}>
          {hasBreak && <TimePicker disabled={!isNew} label="Break start time" value={breakStartTimeMoment} onChange={(m) => setBreakStartTimeMoment(updateMomentWithDate(m))} />}
          {hasBreak && <TimePicker disabled={!isNew} label="Break end time" value={breakEndTimeMoment} onChange={(m) => setBreakEndTimeMoment(updateMomentWithDate(m))} />}
        </Stack>

        <Stack horizontal horizontalAlign="stretch" tokens={{ childrenGap: 6 }}>
          <Stack.Item grow={1}>
            <Dropdown
              label="Service"
              required={true}
              placeholder="Select service"
              disabled={!isNew}
              options={(serviceOfferings.data || [])
                .filter((s) => s.onsiteEnabled)
                .sort((a: ServiceOffering, b: ServiceOffering) => a.durationMinutes - b.durationMinutes)
                .map((s) => ({
                  key: s.code,
                  text: s.name + ' - ' + s.durationMinutes + ' min',
                }))}
              selectedKey={editOnSite.serviceCode}
              onChange={(ev, opt) => opt && update('serviceCode', opt.key)}
            />
          </Stack.Item>
          <Stack.Item grow={1}>
            <TextField
              label="Total number of appointments"
              required={true}
              value={editOnSite.totalAppointments == null ? '' : '' + editOnSite.totalAppointments}
              onChange={(ev, value: any) => {
                const totalAppointments = value || null;
                !isNaN(totalAppointments) && update('totalAppointments', totalAppointments);
              }}
            />
          </Stack.Item>
        </Stack>

        <DoctorSelector label="Heal provider (for EHR only)" required={true} value={editOnSite.leadDoctorId} onChange={(id) => update('leadDoctorId', id)} />

        <Stack horizontal tokens={{ childrenGap: 6 }}>
          <TextField label="Price" prefix="$" value={editOnSite.price === undefined ? '' : editOnSite.price} onChange={(ev, value) => update('price', value)} />
          {!isNew && (
            <Clipboard className="cursor-copy" content={editOnSite.promoCode}>
              <TextField label="Promo code" disabled={true} value={editOnSite.promoCode} />
            </Clipboard>
          )}
        </Stack>

        <TextField label="On-Site instructions (used for booking emails)" value={editOnSite.info} multiline={true} rows={5} onChange={(ev, value) => update('info', value)} />
      </Stack>

      <DialogFooter>
        <PrimaryButton onClick={submit} disabled={!canSubmit()}>
          {isNew ? 'Create' : 'Save'}
        </PrimaryButton>
        <DefaultButton onClick={() => onClose()}>Cancel</DefaultButton>
      </DialogFooter>
    </Panel>
  );
};
